import * as Yup from 'yup';
import type { CurrencyRateData } from '../hooks/useCurrencyConversionRate';
import { addError } from '@shared/utils/logger';
import { roundToPrecision } from '@shared/utils/numbers';

/**
 * Default precision for converted-amount rounding. Matches the value used by
 * `useCurrencyConverter` so live and snapshot conversion paths produce
 * numerically identical inputs for the same math.
 */
export const CONVERSION_PRECISION = 1000;

/**
 * Apply a committed conversion rate to a minor-units amount.
 * Pure math: `roundToPrecision(amount * exchangeRate * taxesAndFeesMultiplier)`.
 * Does not include `fixedCostPerCardInMinorUnits` — callers add that where
 * appropriate (and should guard against doing so for `amount === 0` to keep
 * free items free).
 */
export const applyConversionRate = (
  amount: number,
  { exchangeRate, taxesAndFeesMultiplier }: Pick<CurrencyRateData, 'exchangeRate' | 'taxesAndFeesMultiplier'>,
  precision: number = CONVERSION_PRECISION,
): number => roundToPrecision(amount * exchangeRate * taxesAndFeesMultiplier, precision);

/**
 * Yup schema for validating CurrencyRateData structure
 */
const currencyRateDataSchema: Yup.Schema<CurrencyRateData> = Yup.object({
  exchangeRate: Yup.number().required(),
  taxesAndFeesMultiplier: Yup.number().required(),
  fixedCostPerCardInMinorUnits: Yup.number().required(),
});

/**
 * Parses currencyConversionData JSON from an order into CurrencyRateData format.
 * Returns undefined if the data is null, undefined, or invalid.
 */
export const parseOrderCurrencyConversionData = (currencyConversionData: unknown): CurrencyRateData | undefined => {
  if (!currencyConversionData) {
    return undefined;
  }

  try {
    const parsed = currencyRateDataSchema.validateSync(currencyConversionData, { strict: true });
    return parsed;
  } catch (error) {
    addError('Failed to parse order currency conversion data', { error, currencyConversionData });
    return undefined;
  }
};
